<template>
  <!-- DatepickerV2 -->
  <vue-date-picker
    :model-value="selectedDate"
    :uid="'date-picker'"
    :formats="{ input: format }"
    :locale="dateFnsLocale"
    :min-date="minDate"
    :time-config="{ enableTimePicker: false }"
    :clearable="false"
    :disabled-dates="disabledDates"
    :filters="{ weekDays: disabledWeekDays }"
    :space-confirm="false"
    :position="'left'"
    hide-offset-dates
    :cancel-text="$t('checkout.calendar.cancel')"
    :select-text="$t('checkout.calendar.select')"
    @update:model-value="onSelectDate"
  >
    <template #trigger>
      <div
        class="inline-flex items-center gap-6 px-8 py-6 type-headline-xs text-text-base whitespace-nowrap bg-bg-base border border-border-base hover:bg-bg-subtle hover:border-border-strong cursor-pointer transition-colors"
      >
        <div class="shrink-0">
          <GlobalIcon name="calendar" size="xsmall" />
        </div>
        <div class="shrink-0 whitespace-nowrap"> {{ $t('miniCart.preferredShippingDateShort') + ': ' + displayDate }} </div>
      </div>
    </template>
    <template #day="{ day, date }" :class="getDayClass(date)">
      <div :class="getDayClass(date)">
        {{ day }}
      </div>
      <div v-if="getDayPrice(date) !== null && globalContent.getEnableBreakModeOfDelivery" class="absolute top-36 left-half -translate-x-half type-xxxs text-text-base">
        {{ getDayPrice(date) }}
      </div>
    </template>
    <template #action-extra>
      <div v-if="globalContent.getEnableBreakModeOfDelivery" class="w-full flex items-center justify-start gap-12 type-headline-xs px-8">
        <div class="inline-flex items-center gap-4">
          <div class="w-8 h-8 bg-bg-successStrong rounded-full" />
          <span>{{ $t('checkout.calendar.free') }}</span>
        </div>
        <div v-if="!cartStore.hasDisabledOutsideModFee" class="inline-flex items-center gap-4">
          <div class="w-8 h-8 bg-bg-errorStrong rounded-full" />
          <span>{{ $t('checkout.calendar.outsideMod', { price: outsideMoDPrice }) }}</span>
        </div>
      </div>
    </template>
  </vue-date-picker>
</template>

<script setup lang="ts">
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { ref } from 'vue';
import { useUserStore } from '~/store/user';
import { useGlobalContentStore } from '~/store/globalContent';
import '@vuepic/vue-datepicker/dist/main.css';
import { useCartStore } from '~/store/cart';
import { useLocalizedCurrency } from '~/composables/useLocalizedCurrency';
import { sv } from 'date-fns/locale/sv';
import { enUS } from 'date-fns/locale/en-US';
import { enGB } from 'date-fns/locale/en-GB';
import { de } from 'date-fns/locale/de';
import type { Locale } from 'date-fns';

const globalContent = useGlobalContentStore();
const cartStore = useCartStore();
const userStore = useUserStore();
const { $t } = useNuxtApp();

const dateFnsLocaleMap: Record<string, Locale> = { sv, 'en-US': enUS, 'en-GB': enGB, de };
const dateFnsLocale = computed(() => dateFnsLocaleMap[globalContent.getLocale] ?? enUS);

const props = defineProps<{
  partNo: string,
  date: string,
  defaultDate: string,
  selectedDeliveryPoint?: 'store' | 'address' | 'consumer',
}>();

const emit = defineEmits<{
  (e: 'updatePreferredShippingDate', date: string): void;
}>();

const normalizeToStartOfDay = (date: Date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const parseDateString = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const isoString = value.slice(0, 10);
  const [year, month, day] = isoString.split('-').map(Number);

  if ([year, month, day].some((part) => Number.isNaN(part))) {
    return null;
  }

  return normalizeToStartOfDay(new Date(year, month - 1, day));
};

const selectedDate = ref<Date | null>(parseDateString(props.date));

const orderThresholdDate = computed(() => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const nextYear = currentYear + 1;

  let holidays: Date[] = [...getSwedishRedDays(currentYear), ...getSwedishRedDays(nextYear)];
  if (globalContent.getCountryCode === 'us') {
    holidays = [...getUSHolidays(currentYear), ...getUSHolidays(nextYear)];
  } else if (globalContent.getCountryCode === 'fr') {
    holidays = [...holidays, ...getFrBlackoutDates(currentYear), ...getFrBlackoutDates(nextYear)];
  }

  const isNonBusinessDay = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6 || holidays.some(h => h.toDateString() === date.toDateString());
  };

  const hasCutoffTime = globalContent.orderBeforeHour !== null && globalContent.orderBeforeHour !== undefined;
  const cutoffPassed = hasCutoffTime && (() => {
    const cutoff = new Date(now);
    cutoff.setHours(Number(globalContent.orderBeforeHour), Number(globalContent.orderBeforeMinute ?? 0), 0, 0);
    return now >= cutoff;
  })();

  const earliest = normalizeToStartOfDay(now);

  // If today is a non-business day or the cutoff has passed, advance to the next business day
  if (isNonBusinessDay(earliest) || cutoffPassed) {
    do {
      earliest.setDate(earliest.getDate() + 1);
    } while (isNonBusinessDay(earliest));
  }

  // Count offsetDays as business days, skipping weekends and holidays
  const offsetDays = Number(globalContent.orderBeforeDays ?? 0);
  let counted = 0;
  while (counted < offsetDays) {
    earliest.setDate(earliest.getDate() + 1);
    if (!isNonBusinessDay(earliest)) {
      counted++;
    }
  }

  return earliest;
});

const defaultDateValue = computed(() => parseDateString(props.defaultDate));

const firstSelectableDate = computed(() => {
  const orderThreshold = orderThresholdDate.value;

  if (globalContent.getEnableBreakModeOfDelivery) {
    return orderThreshold;
  }

  const defaultDate = defaultDateValue.value;

  if (!defaultDate) {
    return orderThreshold;
  }

  return orderThreshold > defaultDate ? orderThreshold : defaultDate;
});

const minDate = computed(() => {
  return format(firstSelectableDate.value);
});

const displayDate = computed(() => {
  if (!selectedDate.value) { return ''; }
  return format(selectedDate.value);
});

watch(
  () => props.date,
  (value) => {
    selectedDate.value = parseDateString(value);
  }
);
const format = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

const disabledWeekDays = computed(() => {
  if (!globalContent.getEnableBreakModeOfDelivery && /\d/.test(userStore.userProfile.company.mod ?? '')) {
    const allWeekdays = [0,1,2,3,4,5,6];
    const matches = [...userStore.userProfile.company.mod.matchAll(/[1-7]/g)].map((i) => Number(i[0]));
    return allWeekdays.filter(x => !matches.includes(x));
  }

  return [6,0]; // default -> allow mon-fri delivery
});

const disabledWeekDaysReadOnly = computed(() => {
  if (/\d/.test(userStore.userProfile.company.mod ?? '')) {
    const allWeekdays = [0,1,2,3,4,5,6];
    const matches = [...userStore.userProfile.company.mod.matchAll(/[1-7]/g)].map((i) => Number(i[0]));
    return allWeekdays.filter(x => !matches.includes(x));
  }

  return [6,0]; // default -> allow mon-fri delivery
});

function getSwedishRedDays(year: number = new Date().getFullYear()): Date[] {
  const d = (month: number, day: number) => new Date(year, month - 1, day);

  function getEasterDate(y: number): Date {
    const f = Math.floor;
    const G = y % 19;
    const C = f(y / 100);
    const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
    const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
    const J = (y + f(y / 4) + I + 2 - C + f(C / 4)) % 7;
    const L = I - J;
    const month = 3 + f((L + 40) / 44);
    const day = L + 28 - 31 * f(month / 4);
    return new Date(y, month - 1, day);
  }

  function getMidsummerDay(): Date {
    const startDate = new Date(year, 5, 20); // June 20
    for (let i = 0; i <= 6; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      if (date.getDay() === 6) { return date; }
    }
    throw new Error('Midsummer Day not found');
  }

  function getAllSaintsDay(): Date {
    const startDate = new Date(year, 9, 31); // October 31
    for (let i = 0; i <= 6; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      if (date.getDay() === 6) { return date; }
    }
    throw new Error('All Saints Day not found');
  }

  const easter = getEasterDate(year);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);

  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);

  const ascensionDay = new Date(easter);
  ascensionDay.setDate(easter.getDate() + 39);

  const pentecost = new Date(easter);
  pentecost.setDate(easter.getDate() + 49);

  return [
    d(1, 1),         // New Year's Day
    d(1, 6),         // Epiphany
    goodFriday,      // Good Friday
    easter,          // Easter Sunday
    easterMonday,    // Easter Monday
    d(5, 1),         // Labour Day
    ascensionDay,    // Ascension Day
    pentecost,       // Pentecost
    d(6, 6),         // National Day
    d(6, 19),        // June 19 (warehouse closed)
    getMidsummerDay(), // Midsummer Day (Saturday)
    getAllSaintsDay(), // All Saints' Day (Saturday)
    d(12, 24),       // Christmas Eve (semi-official)
    d(12, 25),       // Christmas Day
    d(12, 26),       // Boxing Day
    d(12, 31),       // New Year's Eve (semi-official)
  ];
}

function getUSHolidays(year: number = new Date().getFullYear()): Date[] {
  const d = (month: number, day: number) => new Date(year, month - 1, day);

  function getEasterDate(y: number): Date {
    const f = Math.floor;
    const G = y % 19;
    const C = f(y / 100);
    const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
    const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
    const J = (y + f(y / 4) + I + 2 - C + f(C / 4)) % 7;
    const L = I - J;
    const month = 3 + f((L + 40) / 44);
    const day = L + 28 - 31 * f(month / 4);
    return new Date(y, month - 1, day);
  }

  // Helper to get nth weekday of month (e.g., 3rd Monday)
  function getNthWeekdayOfMonth(month: number, weekday: number, n: number): Date {
    const firstDay = new Date(year, month - 1, 1);
    const firstWeekday = firstDay.getDay();
    let diff = weekday - firstWeekday;
    if (diff < 0) { diff += 7; }
    const day = 1 + diff + (n - 1) * 7;
    return new Date(year, month - 1, day);
  }

  // Helper to get last weekday of month
  function getLastWeekdayOfMonth(month: number, weekday: number): Date {
    const lastDay = new Date(year, month, 0); // Last day of month
    const lastWeekday = lastDay.getDay();
    let diff = lastWeekday - weekday;
    if (diff < 0) { diff += 7; }
    const day = lastDay.getDate() - diff;
    return new Date(year, month - 1, day);
  }

  const holidays: Date[] = [];

  // New Year's Day + adjacent day based on day of week
  const newYearsDay = d(1, 1);
  holidays.push(newYearsDay);
  const newYearsDayOfWeek = newYearsDay.getDay();
  if (newYearsDayOfWeek === 0) { // Sunday
    holidays.push(d(1, 2)); // Monday after
  } else if (newYearsDayOfWeek === 6) { // Saturday
    holidays.push(d(12, 31)); // Friday before (previous year)
  } else if (newYearsDayOfWeek === 2) { // Tuesday - day before is Monday
    const mondayBefore = new Date(newYearsDay);
    mondayBefore.setDate(newYearsDay.getDate() - 1);
    holidays.push(mondayBefore); // December 31 (Monday)
  } else if (newYearsDayOfWeek === 4) { // Thursday
    holidays.push(d(1, 2)); // Friday after
  }

  // MLK Day - 3rd Monday in January
  holidays.push(getNthWeekdayOfMonth(1, 1, 3));

  // President's Day - 3rd Monday in February
  holidays.push(getNthWeekdayOfMonth(2, 1, 3));

  // Good Friday - 2 days before Easter
  const easter = getEasterDate(year);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  holidays.push(goodFriday);

  // Memorial Day - Last Monday in May
  holidays.push(getLastWeekdayOfMonth(5, 1));

  // Juneteenth - June 19
  holidays.push(d(6, 19));

  // Independence Day - July 4
  holidays.push(d(7, 4));

  // Labor Day - 1st Monday in September
  holidays.push(getNthWeekdayOfMonth(9, 1, 1));

  // Thanksgiving Day - 4th Thursday in November
  holidays.push(getNthWeekdayOfMonth(11, 4, 4));

  // Day after Thanksgiving - Friday after 4th Thursday
  const thanksgiving = getNthWeekdayOfMonth(11, 4, 4);
  const dayAfterThanksgiving = new Date(thanksgiving);
  dayAfterThanksgiving.setDate(thanksgiving.getDate() + 1);
  holidays.push(dayAfterThanksgiving);

  // Christmas Day + adjacent day based on day of week
  const christmasDay = d(12, 25);
  holidays.push(christmasDay);
  const christmasDayOfWeek = christmasDay.getDay();
  if (christmasDayOfWeek === 0) { // Sunday
    holidays.push(d(12, 26)); // Monday after
  } else if (christmasDayOfWeek === 6) { // Saturday
    holidays.push(d(12, 24)); // Friday before
  } else if (christmasDayOfWeek === 2) { // Tuesday - day before is Monday
    holidays.push(d(12, 24)); // Monday before
  } else if (christmasDayOfWeek === 4) { // Thursday
    holidays.push(d(12, 26)); // Friday after
  }

  return holidays;
}

function getFrBlackoutDates(year: number = new Date().getFullYear()): Date[] {
  return [
    new Date(year, 7, 13), // August 13
    new Date(year, 7, 17), // August 17
  ];
}

// Select appropriate holiday function based on country
const disabledDates = computed(() => {
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const swedishHolidays = [...getSwedishRedDays(currentYear), ...getSwedishRedDays(nextYear)];

  if (globalContent.getCountryCode === 'us') {
    // Include both current and next year holidays
    const holidays = [...getUSHolidays(currentYear), ...getUSHolidays(nextYear)];
    return holidays;
  }

  if (globalContent.getCountryCode === 'fr') {
    return [...swedishHolidays, ...getFrBlackoutDates(currentYear), ...getFrBlackoutDates(nextYear)];
  }

  return swedishHolidays;
});

const getDayPrice = (date: Date) => {
  const dayOfWeek = date.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isHoliday = disabledDates.value.some(
    d => d.toDateString() === date.toDateString()
  );

  // Check if date is in the past
  const isBeforeCutoff = normalizeToStartOfDay(date) < firstSelectableDate.value;

  // Don't show price for disabled dates (weekends, holidays, past)
  if (isWeekend || isHoliday || isBeforeCutoff) {
    return null;
  }

  // Check if this day is outside MoD
  const isOutsideMoD = disabledWeekDaysReadOnly.value.includes(dayOfWeek);

  // When delivery point is 'address', all dates are free (show 0)
  if (globalContent.getEnableBreakModeOfDelivery && props.selectedDeliveryPoint === 'address') {
    return useLocalizedCurrency(0, true);
  }

  if (cartStore.hasDisabledOutsideModFee) {
    return useLocalizedCurrency(0, true);
  }

  // Otherwise: Outside MoD = 150, Inside MoD = 0
  return isOutsideMoD ? useLocalizedCurrency(globalContent.outsideModDeliveryFeePrice as number, true) : useLocalizedCurrency(0, true);
};

const getDayClass = (date: Date) => {
  const dayOfWeek = date.getDay();
  const isOutsideMoD = disabledWeekDaysReadOnly.value.includes(dayOfWeek);
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isHoliday = disabledDates.value.some(
    d => d.toDateString() === date.toDateString()
  );

  const isBeforeCutoff = normalizeToStartOfDay(date) < firstSelectableDate.value;

  if (isWeekend || isHoliday || isBeforeCutoff) {
    return 'day-disabled';
  }

  if (globalContent.getEnableBreakModeOfDelivery && isOutsideMoD && !cartStore.hasDisabledOutsideModFee) {
    return 'day-outside-mod';
  }

  return 'day-inside-mod';
};

const getDayClassForMinDate = (date: Date) => {
  const dayOfWeek = date.getDay();
  const isOutsideMoD = disabledWeekDaysReadOnly.value.includes(dayOfWeek);
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isHoliday = disabledDates.value.some(
    d => d.toDateString() === date.toDateString()
  );

  const isBeforeCutoff = normalizeToStartOfDay(date) < firstSelectableDate.value;

  if (isWeekend || isHoliday || isBeforeCutoff) {
    return 'day-disabled';
  }

  if (isOutsideMoD) {
    return 'day-outside-mod';
  }

  return 'day-inside-mod';
};

const onSelectDate = (modelData: Date | null) => {
  selectedDate.value = modelData;

  if (selectedDate.value) {
    emit('updatePreferredShippingDate', format(selectedDate.value));
  }
};

// Price for outside MoD legend (depends on delivery point)
const outsideMoDPrice = computed(() => {
  return props.selectedDeliveryPoint === 'address' || cartStore.hasDisabledOutsideModFee ? useLocalizedCurrency(0, true) : useLocalizedCurrency(globalContent.outsideModDeliveryFeePrice as number, true);
});

</script>
<style scoped>

:deep(.dp__calendar) {
  font-family: "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 14px;
  font-weight: 500;
}

:deep(.dp--future) {
  color: #2C573D;
  background: #E6F5EC;

  &:hover:not(.dp__cell_disabled, .dp__active_date) {
    color: #FFFFFF;
    background: #548A69;
  }
}

:deep(.dp__cell_disabled) {
  color: #8A8681;
  background: #FAF5EF;
}

:deep(.dp__active_date) {
  color: #FFFFFF;
  background: #2C573D;
  outline-width: 2px;
  outline-color: #2C573D;
  outline-style: solid;
  outline-offset: 1px;
}

:deep(.dp--future):has(.day-outside-mod) {
  color: #572C2C;
  background: #F5E6E6;

  &:hover:not(.dp__cell_disabled, .dp__active_date) {
    color: #FFFFFF;
    background: #A35353;
  }
}

:deep(.dp__active_date):has(.day-outside-mod) {
  color: #FFFFFF;
  background: #572C2C;
  outline-color: #572C2C;
}

:deep(.dp__calendar_row) {
  @apply my-24 first:mt-12;
  margin: 24px 0;
  gap: 12px;
}

:deep(.dp__calendar_item) {
  position: relative;
}

:deep(.dp__today) {
  border-color: #547E8A;
}

:deep(.dp__calendar_header) {
  @apply uppercase font-open text-text-subtle font-medium tracking-wide;
}

:deep(.dp__action_button) {
  font-family: "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 2px 8px;
  height: auto;
}

:deep(.dp__action_select) {
  background: #2e2d2b;
  &:hover {
    background: #4D4A48;
  }
}

:deep(.dp__outer_menu_wrap) {
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
}
</style>
